import React, { useState } from 'react';
import {
  Input,
  Button,
  Space,
  Row,
  Col,
  Typography,
  message,
  Table,
  Card,
  Tag,
  Empty,
  Spin,
  Radio
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined
} from '@ant-design/icons';
import API from '../services/api';

const { Title, Text } = Typography;

const AdminFilterRuleQuery = () => {
  const [serverIp, setServerIp] = useState('');
  const [loading, setLoading] = useState(false);
  const [filterRules, setFilterRules] = useState([]);
  const [hasQueried, setHasQueried] = useState(false);
  const [queryType, setQueryType] = useState('firewall'); // 'firewall' 或 'cc'

  // IP地址输入框变化处理
  const handleIpChange = (e) => {
    setServerIp(e.target.value);
  };

  // IP地址格式验证
  const validateIpFormat = (ip) => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  // 查询过滤规则
  const handleQueryFilterRules = async () => {
    if (!serverIp.trim()) {
      message.warning('请输入服务器IP地址');
      return;
    }

    if (!validateIpFormat(serverIp.trim())) {
      message.error('请输入有效的IP地址格式');
      return;
    }

    setLoading(true);
    try {
      // 根据查询类型选择不同的API接口
      const apiEndpoint = queryType === 'cc' ? '/api/query-filter-rule-cc' : '/api/query-filter-rule';
      const response = await API.post(apiEndpoint, {
        server_ip: serverIp.trim()
      });

      // 处理响应数据
      if (response.data) {
        const { data, msg } = response.data;
        
        // 处理返回的规则数据
        if (data && Array.isArray(data)) {
          setFilterRules(data);
        } else if (data && typeof data === 'object') {
          // 如果data是对象，尝试提取规则列表
          setFilterRules(data.rules || [data]);
        } else {
          setFilterRules([]);
        }
        
        setHasQueried(true);
        const queryTypeName = queryType === 'cc' ? 'CC墙' : '大墙';
        message.success(msg || `查询${queryTypeName}过滤规则成功`);
      } else {
        message.error('查询失败，未返回有效数据');
        setFilterRules([]);
      }
    } catch (error) {
      console.error('查询过滤规则失败:', error);
      
      const queryTypeName = queryType === 'cc' ? 'CC墙' : '大墙';
      
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.response?.status === 403) {
        message.error('没有管理员权限');
      } else if (error.response?.status === 400) {
        message.error('server_ip参数不能为空');
      } else {
        message.error(`查询${queryTypeName}过滤规则时出错`);
      }
      
      setFilterRules([]);
      setHasQueried(true);
    } finally {
      setLoading(false);
    }
  };

  // 重置查询
  const handleReset = () => {
    setServerIp('');
    setFilterRules([]);
    setHasQueried(false);
  };

  // 查询类型改变处理
  const handleQueryTypeChange = (e) => {
    setQueryType(e.target.value);
    // 如果已经查询过，清空结果
    if (hasQueried) {
      setFilterRules([]);
      setHasQueried(false);
    }
  };

  // 格式化端口列表显示
  const formatPortList = (portList) => {
    if (!portList || portList.length === 0) {
      return <Text type="secondary">全部端口</Text>;
    }
    
    const portRanges = portList.map(port => {
      if (port.start === port.end) {
        return port.start.toString();
      } else {
        return `${port.start}-${port.end}`;
      }
    });
    
    return (
      <div>
        {portRanges.map((range, index) => (
          <Tag key={index} size="small" style={{ marginBottom: '2px' }}>
            {range}
          </Tag>
        ))}
      </div>
    );
  };

  // 格式化协议显示
  const formatProtocol = (protocol) => {
    const protocolMap = {
      0: { text: '全部', color: 'purple' },
      1: { text: 'ICMP', color: 'green' },
      6: { text: 'TCP', color: 'blue' },
      17: { text: 'UDP', color: 'orange' }
    };
    
    const config = protocolMap[protocol] || { text: `协议${protocol}`, color: 'default' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 格式化IP范围显示
  const formatIpRange = (rule) => {
    if (rule.server_ip_type === 'all') {
      return <Text type="secondary">所有IP</Text>;
    } else if (rule.server_ip_type === 'single') {
      return <Text code>{rule.server_ip_start}</Text>;
    } else if (rule.server_ip_type === 'range') {
      if (rule.server_ip_start === rule.server_ip_end) {
        return <Text code>{rule.server_ip_start}</Text>;
      } else {
        return (
          <div>
            <Text code>{rule.server_ip_start}</Text>
            <br />
            <Text type="secondary">至</Text>
            <br />
            <Text code>{rule.server_ip_end}</Text>
          </div>
        );
      }
    }
    return <Text type="secondary">-</Text>;
  };

  // 格式化动作显示
  const formatAction = (action) => {
    const actionMap = {
      'deny': { text: '拒绝', color: 'red' },
      'allow': { text: '允许', color: 'green' },
      'drop': { text: '丢弃', color: 'volcano' }
    };
    
    const config = actionMap[action] || { text: action, color: 'default' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 格式化客户端IP类型显示
  const formatClientIpType = (rule) => {
    // 优先检查新的字段名 clienr_ip_type（注意这里可能是拼写错误）
    const clientIpType = rule.clienr_ip_type || rule.client_ip_type;
    
    if (clientIpType === 'all') {
      return <Tag color="default">所有IP</Tag>;
    } else if (clientIpType === 'exclude_position') {
      // 显示排除的地理位置
      if (rule.exclude_position && rule.exclude_position.length > 0) {
        return (
          <div>
            <Tag color="orange">排除地区</Tag>
            <br />
            {rule.exclude_position.map((position, index) => (
              <Tag key={index} size="small" color="volcano" style={{ marginTop: '2px' }}>
                {position}
              </Tag>
            ))}
          </div>
        );
      } else {
        return <Tag color="orange">排除地区</Tag>;
      }
    } else if (clientIpType) {
      return <Tag color="cyan">{clientIpType}</Tag>;
    } else {
      return <Tag color="default">所有IP</Tag>;
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '索引',
      dataIndex: 'index',
      key: 'index',
      width: '6%',
      sorter: (a, b) => a.index - b.index,
      render: (text) => <Text code>#{text}</Text>
    },
    {
      title: '规则名称',
      dataIndex: 'name',
      key: 'name',
      width: '18%',
      ellipsis: true,
      render: (text) => (
        <Text strong style={{ color: '#1890ff' }}>
          {text || '未命名规则'}
        </Text>
      )
    },
    {
      title: '协议',
      dataIndex: 'protocol',
      key: 'protocol',
      width: '8%',
      filters: [
        { text: '全部', value: 0 },
        { text: 'ICMP', value: 1 },
        { text: 'TCP', value: 6 },
        { text: 'UDP', value: 17 }
      ],
      onFilter: (value, record) => record.protocol === value,
      render: formatProtocol
    },
    {
      title: '方向',
      dataIndex: 'direction',
      key: 'direction',
      width: '8%',
      filters: [
        { text: '入站', value: 'in' },
        { text: '出站', value: 'out' }
      ],
      onFilter: (value, record) => record.direction === value,
      render: (direction) => (
        <Tag color={direction === 'in' ? 'blue' : 'purple'}>
          {direction === 'in' ? '入站' : '出站'}
        </Tag>
      )
    },
    {
      title: '动作',
      dataIndex: 'action',
      key: 'action',
      width: '8%',
      filters: [
        { text: '拒绝', value: 'deny' },
        { text: '允许', value: 'allow' },
        { text: '丢弃', value: 'drop' }
      ],
      onFilter: (value, record) => record.action === value,
      render: formatAction
    },
    {
      title: '服务器IP',
      key: 'server_ip_range',
      width: '13%',
      ellipsis: true,
      render: (_, record) => formatIpRange(record)
    },
    {
      title: '服务器端口',
      dataIndex: 'server_port_list',
      key: 'server_port_list',
      width: '12%',
      render: formatPortList
    },
    {
      title: '客户端端口',
      dataIndex: 'client_port_list',
      key: 'client_port_list',
      width: '12%',
      render: formatPortList
    },
    {
      title: '客户端IP',
      key: 'client_ip_info',
      width: '15%',
      render: (_, record) => formatClientIpType(record)
    }
  ];

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: 'calc(100vh - 64px)' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <Title level={2} style={{ marginBottom: '24px' }}>
          <FilterOutlined style={{ marginRight: '8px' }} />
          IP过滤规则查询
        </Title>

                {/* 查询区域 */}
        <Card style={{ marginBottom: '24px' }}>
          <Row gutter={[16, 16]} align="middle">
            <Col span={24}>
              <Space align="center" wrap>
                <Text strong>查询类型：</Text>
                <Radio.Group 
                  value={queryType} 
                  onChange={handleQueryTypeChange}
                  buttonStyle="solid"
                >
                  <Radio.Button value="firewall">
                    <FilterOutlined style={{ marginRight: '4px' }} />
                    大墙过滤规则
                  </Radio.Button>
                  <Radio.Button value="cc">
                    <FilterOutlined style={{ marginRight: '4px' }} />
                    CC墙过滤规则
                  </Radio.Button>
                </Radio.Group>
              </Space>
            </Col>
            <Col span={24}>
              <Space align="center" wrap>
                <Text strong>服务器IP地址：</Text>
                <Input
                  placeholder="请输入服务器IP地址（例：192.168.1.1）"
                  style={{ width: '300px' }}
                  value={serverIp}
                  onChange={handleIpChange}
                  onPressEnter={handleQueryFilterRules}
                  prefix={<SearchOutlined />}
                />
                <Button
                  type="primary"
                  onClick={handleQueryFilterRules}
                  loading={loading}
                  disabled={!serverIp.trim()}
                  icon={<SearchOutlined />}
                >
                  查询{queryType === 'cc' ? 'CC墙' : '大墙'}规则
                </Button>
                <Button
                  onClick={handleReset}
                  disabled={!hasQueried}
                  icon={<ReloadOutlined />}
                >
                  重置
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* 结果显示区域 */}
        {hasQueried && (
          <Card>
            <div style={{ marginBottom: '16px' }}>
              <Title level={4} style={{ margin: 0 }}>
                {queryType === 'cc' ? 'CC墙' : '大墙'}过滤规则查询结果 {serverIp && `- ${serverIp}`}
              </Title>
              {filterRules.length > 0 && (
                <Text type="secondary">
                  共找到 {filterRules.length} 条{queryType === 'cc' ? 'CC墙' : '大墙'}过滤规则
                </Text>
              )}
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px' }}>
                  <Text type="secondary">正在查询{queryType === 'cc' ? 'CC墙' : '大墙'}过滤规则...</Text>
                </div>
              </div>
            ) : filterRules.length > 0 ? (
              <Table
                columns={columns}
                dataSource={filterRules}
                rowKey={(record, idx) => record.index || idx}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `第 ${range[0]}-${range[1]} 条，共 ${total} 条规则`,
                }}
                size="middle"
                tableLayout="fixed"
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span>
                    暂无{queryType === 'cc' ? 'CC墙' : '大墙'}过滤规则数据
                    <br />
                    <Text type="secondary">该服务器IP暂未配置{queryType === 'cc' ? 'CC墙' : '大墙'}过滤规则</Text>
                  </span>
                }
              />
            )}
          </Card>
        )}


      </div>
    </div>
  );
};

export default AdminFilterRuleQuery; 